import { StateCreator } from 'zustand';
import { get as lodashGet, set as lodashSet, cloneDeep } from 'lodash';

/**
 * A utility type to convert the keys of a deeply nested object to a string path.
 * Example: { a: { b: { c: string } } } will become "a" | "a.b" | "a.b.c".
 */
type PathToString<T> = T extends object
  ? {
      [K in keyof T & (string | number)]: T[K] extends object
        ? `${K}` | `${K}.${PathToString<T[K]>}`
        : `${K}`;
    }[keyof T & (string | number)]
  : never;

/**
 * A utility type to infer the type of a nested property value based on a given path.
 * Example: For the path "a.b.c" in { a: { b: { c: string } } }, it will infer string.
 */
type NewValueType<T, P extends string> = P extends `${infer Key}.${infer Rest}`
  ? Key extends keyof T
    ? NewValueType<T[Key], Rest>
    : never
  : P extends keyof T
    ? T[P]
    : never;

/**
 * The structure of the store, which holds data and provides methods to get, update, and reset parts of the state.
 */
export interface StoreType<T = Record<string, any>> {
  data: T;

  /**
   * Get the value from the store at the given path, with a default value if not found.
   * @param path - The path to the property (e.g., "a.b.c").
   * @param defaultValue - The default value to return if the property does not exist.
   * @returns The value at the given path, or the default value if not found.
   */
  get: <P extends PathToString<T>>(
    path: P,
    defaultValue?: NewValueType<T, P>,
  ) => NewValueType<T, P>;

  /**
   * Update the value of a property in the store at the given path.
   * @param path - The path to the property (e.g., "a.b.c").
   * @param value - The new value to set at the given path.
   */
  update: <P extends PathToString<T>>(
    path: P,
    value: NewValueType<T, P>,
  ) => void;

  /**
   * Reset the store to its initial state, with optional exclusion of some properties from being reset.
   * @param exclude - An optional array of paths to exclude from reset (e.g., ["a.b"]).
   */
  reset: <P extends PathToString<T>>(exclude?: P[]) => void;
  resetSelective: <P extends PathToString<T>>(include?: P[]) => void;
}

/**
 * A utility function to create a slice of the Zustand store, with methods for getting, updating, and resetting state.
 *
 * @param initialData - The initial data for the store.
 * @returns A Zustand slice with methods for interacting with the store's state.
 */
export const createSlice =
  <T>(initialData: T = {} as T): StateCreator<StoreType<T>> =>
  (_set, _get) => ({
    data: cloneDeep(initialData) as T,

    update: <P extends PathToString<T>>(path: P, value: NewValueType<T, P>) => {
      // Updates the state at the given path
      _set(({ data }) => ({ data: lodashSet<T>(data as object, path, value) }));
    },

    get: <P extends PathToString<T>>(
      path: P,
      defaultValue?: NewValueType<T, P>,
    ) => {
      // Retrieves the value from the store at the given path
      return lodashGet(_get().data, path, defaultValue) as NewValueType<T, P>;
    },

    // Reset the store to its initial state, excluding selected keys
    reset: <P extends PathToString<T>>(exclude?: P[]) =>
      _set(({ data }) => {
        // Resets the state, excluding the properties specified in the 'exclude' array
        // data -> modifiedData
        const excludeObject = cloneDeep(initialData);
        exclude?.forEach((path) => {
          lodashSet(excludeObject as object, path, lodashGet(data, path));
        });
        return {
          data: {
            ...excludeObject,
          } as T,
        };
      }),

    // Reset only selected keys in the store
    resetSelective: <P extends PathToString<T>>(include?: P[]) =>
      _set(({ data }) => {
        // Resets the state only for keys in the include array
        // data -> modifiedData, for selected keys, the value will be replaced by initialData
        const includeObject = cloneDeep(data);
        include?.forEach((path) => {
          lodashSet(
            includeObject as object,
            path,
            lodashGet(initialData, path),
          );
        });
        return {
          data: {
            ...includeObject,
          } as T,
        };
      }),
  });
